import { useEffect, useMemo, useState } from "react";
import { api, euro } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { TargetIcon as Target, FloppyDiskIcon as FloppyDisk, PlusIcon as Plus, TrashIcon as Trash, CheckCircleIcon as CheckCircle, WarningCircleIcon as WarningCircle, CopyIcon as Copy } from "@phosphor-icons/react";

const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const emptyMesi = () => Array.from({ length: 12 }, (_, i) => ({ mese: i + 1, percentuale: 0, note: "" }));

export function ReportTargetSection() {
  const [years, setYears] = useState([]);
  const [anno, setAnno] = useState(null);
  const [cfg, setCfg] = useState(null); // { anno, target_annuo, mesi:[{mese,percentuale,note}], note }
  const [saving, setSaving] = useState(false);
  const [newAnno, setNewAnno] = useState(String(new Date().getFullYear() + 1));

  const loadList = async (selectAnno) => {
    const { data } = await api.get("/impostazioni/reportistica/target-assistenza");
    setYears(data || []);
    const pick = selectAnno || (data && data[0] && data[0].anno);
    if (pick) selectYear(pick, data);
  };

  const selectYear = (a, list) => {
    const src = (list || years).find((y) => y.anno === a);
    setAnno(a);
    if (src) setCfg({ anno: src.anno, target_annuo: src.target_annuo, note: src.note || "", mesi: src.mesi.map((m) => ({ mese: m.mese, percentuale: m.percentuale, note: m.note || "" })) });
  };

  useEffect(() => { loadList(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totPerc = useMemo(() => cfg ? Math.round(cfg.mesi.reduce((s, m) => s + (parseFloat(m.percentuale) || 0), 0) * 100) / 100 : 0, [cfg]);
  const percValid = Math.abs(totPerc - 100) <= 0.05;
  const annuoValid = cfg && parseFloat(cfg.target_annuo) > 0;
  const canSave = cfg && percValid && annuoValid;
  const totTarget = useMemo(() => cfg ? cfg.mesi.reduce((s, m) => s + (parseFloat(cfg.target_annuo) || 0) * (parseFloat(m.percentuale) || 0) / 100, 0) : 0, [cfg]);

  const setPerc = (idx, v) => setCfg((c) => ({ ...c, mesi: c.mesi.map((m, i) => i === idx ? { ...m, percentuale: v } : m) }));
  const setNote = (idx, v) => setCfg((c) => ({ ...c, mesi: c.mesi.map((m, i) => i === idx ? { ...m, note: v } : m) }));

  const save = async () => {
    if (!canSave) { toast.error("La somma delle percentuali mensili deve essere pari a 100% e il target annuo maggiore di 0."); return; }
    setSaving(true);
    try {
      const payload = { anno: cfg.anno, target_annuo: parseFloat(cfg.target_annuo), note: cfg.note || "", mesi: cfg.mesi.map((m) => ({ mese: m.mese, percentuale: parseFloat(m.percentuale) || 0, note: m.note || "" })) };
      await api.put(`/impostazioni/reportistica/target-assistenza/${cfg.anno}`, payload);
      toast.success(`Target ${cfg.anno} salvato`);
      await loadList(cfg.anno);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  const createYear = async (duplicate) => {
    const a = parseInt(newAnno, 10);
    if (!a || a < 2000 || a > 2100) { toast.error("Anno non valido"); return; }
    if (years.some((y) => y.anno === a)) { toast.error("Esiste già un target per questo anno"); return; }
    const baseMesi = duplicate && cfg ? cfg.mesi.map((m) => ({ mese: m.mese, percentuale: parseFloat(m.percentuale) || 0, note: "" })) : emptyMesi();
    const baseAnnuo = duplicate && cfg ? parseFloat(cfg.target_annuo) || 0 : 0;
    try {
      // se non duplico e le percentuali sono 0, il backend rifiuterebbe (somma != 100): creo lo scheletro localmente
      if (!duplicate) {
        setYears((ys) => [{ anno: a, target_annuo: 0, mesi: baseMesi, note: "", totale_percentuale: 0 }, ...ys]);
        setAnno(a); setCfg({ anno: a, target_annuo: 0, note: "", mesi: baseMesi });
        toast.info(`Bozza anno ${a} creata: imposta target e percentuali, poi salva.`);
        return;
      }
      await api.post("/impostazioni/reportistica/target-assistenza", { anno: a, target_annuo: baseAnnuo, note: "", mesi: baseMesi });
      toast.success(`Target ${a} creato duplicando ${cfg.anno}`);
      await loadList(a);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nella creazione"); }
  };

  const removeYear = async () => {
    if (!cfg) return;
    if (!window.confirm(`Eliminare il target ${cfg.anno}?`)) return;
    try {
      await api.delete(`/impostazioni/reportistica/target-assistenza/${cfg.anno}`);
      toast.success(`Target ${cfg.anno} eliminato`);
      const rest = years.filter((y) => y.anno !== cfg.anno);
      setYears(rest);
      if (rest[0]) selectYear(rest[0].anno, rest); else { setCfg(null); setAnno(null); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nell'eliminazione"); }
  };

  return (
    <Card className="rounded-sm border-border shadow-none p-6" data-testid="report-target-section">
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Target size={20} weight="bold" className="text-primary" /></div>
          <div>
            <h3 className="font-heading font-semibold">Target Assistenza Tecnica</h3>
            <p className="text-xs text-muted-foreground">Configurazione budget economico annuale e ripartizione mensile per la reportistica ore.</p>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Anno</Label>
            <Select value={anno ? String(anno) : ""} onValueChange={(v) => selectYear(parseInt(v, 10))}>
              <SelectTrigger className="mt-1 rounded-sm h-9 w-[120px]" data-testid="target-year-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>{years.map((y) => <SelectItem key={y.anno} value={String(y.anno)}>{y.anno}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Nuovo anno</Label>
            <Input type="number" value={newAnno} onChange={(e) => setNewAnno(e.target.value)} className="mt-1 rounded-sm h-9 w-[100px]" data-testid="target-new-year-input" />
          </div>
          <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => createYear(false)} data-testid="target-create-year"><Plus size={15} /> Crea</Button>
          {cfg && <Button variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => createYear(true)} data-testid="target-duplicate-year"><Copy size={15} /> Duplica</Button>}
        </div>
      </div>

      {!cfg ? (
        <p className="text-sm text-muted-foreground">Nessun target configurato. Crea un nuovo anno per iniziare.</p>
      ) : (
        <>
          {/* Card riepilogo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
            <div className="rounded-sm border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Anno</div>
              <div className="font-heading text-2xl font-bold mt-1">{cfg.anno}</div>
            </div>
            <div className="rounded-sm border border-border p-3">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Target annuo (€)</Label>
              <Input type="number" step="0.01" value={cfg.target_annuo} onChange={(e) => setCfg((c) => ({ ...c, target_annuo: e.target.value }))} className="mt-1 rounded-sm h-9 tabular-nums" data-testid="target-annuo-input" />
            </div>
            <div className="rounded-sm border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Totale percentuali</div>
              <div className={`font-heading text-2xl font-bold mt-1 tabular-nums ${percValid ? "text-emerald-600" : "text-rose-600"}`} data-testid="target-total-perc">{totPerc.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</div>
              <div className="text-[10px] text-muted-foreground">Somma target: {euro(totTarget)}</div>
            </div>
            <div className="rounded-sm border border-border p-3 flex flex-col justify-center">
              {percValid && annuoValid ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium" data-testid="target-valid"><CheckCircle size={18} weight="fill" /> Configurazione valida</span>
              ) : (
                <span className="inline-flex items-center gap-1.5 text-sm text-amber-600 font-medium" data-testid="target-invalid"><WarningCircle size={18} weight="fill" /> {!annuoValid ? "Target annuo mancante" : "Percentuali ≠ 100%"}</span>
              )}
            </div>
          </div>
          {!percValid && <p className="text-sm text-amber-600 mb-3" data-testid="target-perc-warning">La somma delle percentuali mensili deve essere pari a 100%.</p>}

          {/* Tabella mensile */}
          <div className="overflow-x-auto rounded-sm border border-border">
            <table className="w-full text-sm" data-testid="target-months-table">
              <thead className="bg-secondary/40 text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Mese</th>
                  <th className="px-3 py-2 text-right font-semibold w-[140px]">Percentuale (%)</th>
                  <th className="px-3 py-2 text-right font-semibold w-[160px]">Target mensile (€)</th>
                  <th className="px-3 py-2 text-left font-semibold">Note</th>
                </tr>
              </thead>
              <tbody>
                {cfg.mesi.map((m, i) => (
                  <tr key={m.mese} className="border-b border-border/60" data-testid={`target-month-row-${m.mese}`}>
                    <td className="px-3 py-1.5 font-medium">{MESI[m.mese]}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" step="0.01" value={m.percentuale} onChange={(e) => setPerc(i, e.target.value)} className="rounded-sm h-8 text-right tabular-nums" data-testid={`target-perc-${m.mese}`} />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium" data-testid={`target-mensile-${m.mese}`}>
                      {euro((parseFloat(cfg.target_annuo) || 0) * (parseFloat(m.percentuale) || 0) / 100)}
                    </td>
                    <td className="px-3 py-1.5">
                      <Input value={m.note} onChange={(e) => setNote(i, e.target.value)} className="rounded-sm h-8" placeholder="—" data-testid={`target-note-${m.mese}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-5">
            <Button variant="outline" className="rounded-sm gap-1.5 text-destructive hover:text-destructive" onClick={removeYear} data-testid="target-delete-year"><Trash size={15} /> Elimina anno</Button>
            <Button className="rounded-sm gap-1.5" onClick={save} disabled={!canSave || saving} data-testid="target-save"><FloppyDisk size={16} /> {saving ? "Salvataggio…" : "Salva configurazione"}</Button>
          </div>
        </>
      )}
    </Card>
  );
}
